import { Injectable } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { GenericConfirmationModalComponent } from './generic-confirmation-modal.component'

@Injectable({
  providedIn: 'root',
})
export class GenericConfirmationModalService {
  constructor(private readonly modalService: NgbModal) {}

  /**
   * Opens a generic confirmation modal with customizable options
   *
   * @param options Configuration options for the modal
   * @param options.title Optional title for the modal
   * @param options.message The message to display in the modal
   * @param options.confirmButtonText Optional text for the confirm button
   * @param options.cancelButtonText Optional text for the cancel button
   * @param options.confirmButtonClass Optional CSS class for the confirm button
   * @param options.cancelButtonClass Optional CSS class for the cancel button
   * @returns Promise resolving to true if confirmed, false if dismissed
   */
  confirm(options: {
    title?: string
    message: string
    confirmButtonText?: string
    cancelButtonText?: string
    confirmButtonClass?: string
    cancelButtonClass?: string
  }): Promise<boolean> {
    const modalRef = this.modalService.open(GenericConfirmationModalComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
    })

    const component =
      modalRef.componentInstance as GenericConfirmationModalComponent

    if (options.title) {
      component.confirmationTitle = options.title
    }

    component.confirmationText = options.message

    if (options.confirmButtonText) {
      component.confirmButtonText = options.confirmButtonText
    }

    if (options.cancelButtonText) {
      component.cancelButtonText = options.cancelButtonText
    }

    if (options.confirmButtonClass) {
      component.confirmButtonClass = options.confirmButtonClass
    }

    if (options.cancelButtonClass) {
      component.cancelButtonClass = options.cancelButtonClass
    }

    return modalRef.result.then(
      result => !!result,
      () => false,
    )
  }

  /**
   * Opens a warning confirmation modal
   *
   * @param message The warning message to display
   * @param title Optional title (defaults to "Warning")
   * @returns Promise resolving to true if confirmed, false if dismissed
   */
  warning(message: string, title: string = 'Warning'): Promise<boolean> {
    return this.confirm({
      title,
      message,
      confirmButtonClass: 'btn-warning',
      confirmButtonText: 'Proceed',
    })
  }

  /**
   * Opens a success confirmation modal
   *
   * @param message The success message to display
   * @param title Optional title (defaults to "Success")
   * @returns Promise resolving to true if confirmed, false if dismissed
   */
  success(message: string, title: string = 'Success'): Promise<boolean> {
    return this.confirm({
      title,
      message,
      confirmButtonClass: 'btn-success',
      confirmButtonText: 'OK',
    })
  }

  /**
   * Opens an error confirmation modal
   *
   * @param message The error message to display
   * @param title Optional title (defaults to "Error")
   * @returns Promise resolving to true if confirmed, false if dismissed
   */
  error(message: string, title: string = 'Error'): Promise<boolean> {
    return this.confirm({
      title,
      message,
      confirmButtonClass: 'btn-danger',
      confirmButtonText: 'OK',
    })
  }
}
